import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

export default function BrandProfilePage() {
  const { tenantId } = useParams();
  const { user } = useAuth();
  const tid = tenantId || user?.tenant_id;
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tid || tid === 'system') return setLoading(false);
    api.get(`/tenants/${tid}/brand`)
      .then((r) => { setBrand(r.data); setLoading(false); })
      .catch(() => { setBrand({}); setLoading(false); });
  }, [tid]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/tenants/${tid}/brand`, brand);
      setBrand(res.data);
      toast.success('Brand profile saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Loading brand profile...</div>;
  if (!brand) return <div className="text-[#737373] font-ibm">No brand profile available</div>;

  const update = (key, value) => setBrand((prev) => ({ ...prev, [key]: value }));

  return (
    <div data-testid="brand-profile-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Marka Profili</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">AI üretilen içerikler için marka sesinizi tanımlayın</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-brand-btn" className="bg-white text-black hover:bg-[#E5E5E5] active:scale-[0.98] transition-transform duration-100">
          {saving ? 'Kaydediliyor...' : 'Profili Kaydet'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white">Kimlik</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Marka Adı</Label>
              <Input value={brand.brand_name || ''} onChange={(e) => update('brand_name', e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="brand-name-input" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Sektör</Label>
              <Input value={brand.industry || ''} onChange={(e) => update('industry', e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="brand-industry-input" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Hedef Kitle</Label>
              <Textarea value={brand.target_audience || ''} onChange={(e) => update('target_audience', e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[80px]" data-testid="brand-audience-input" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white">Ses ve Ton</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Ton</Label>
              <Select value={brand.tone || 'Professional'} onValueChange={(v) => update('tone', v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="brand-tone-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {['Professional', 'Casual', 'Humorous', 'Inspirational', 'Educational', 'Bold', 'Friendly'].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">CTA Tarzı</Label>
              <Select value={brand.cta_style || 'Direct'} onValueChange={(v) => update('cta_style', v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="brand-cta-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {['Direct', 'Soft', 'Question', 'Urgency', 'Value-driven'].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Diller (virgülle ayrılmış)</Label>
              <Input value={(brand.languages || []).join(', ')} onChange={(e) => update('languages', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="brand-languages-input" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white">Koruma Kuralları</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Yasaklı Kelimeler (virgülle ayrılmış)</Label>
              <Textarea value={(brand.forbidden_words || []).join(', ')} onChange={(e) => update('forbidden_words', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[80px] font-jetbrains text-sm" data-testid="brand-forbidden-input" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">İzin Verilen İddialar (virgülle ayrılmış)</Label>
              <Textarea value={(brand.allowed_claims || []).join(', ')} onChange={(e) => update('allowed_claims', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className="bg-[#0A0A0A] border-[#262626] text-white mt-1 min-h-[80px] font-jetbrains text-sm" data-testid="brand-claims-input" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white">Görsel Kimlik</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Logo URL</Label>
              <Input value={brand.logo_url || ''} onChange={(e) => update('logo_url', e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="brand-logo-input" placeholder="https://..." />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Marka Renkleri (virgülle ayrılmış hex)</Label>
              <Input value={(brand.brand_colors || []).join(', ')} onChange={(e) => update('brand_colors', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="brand-colors-input" placeholder="#FACC15, #0A0A0A" />
            </div>
            {brand.brand_colors?.length > 0 && (
              <div className="flex gap-2 mt-2">
                {brand.brand_colors.map((c, i) => (
                  <div key={i} className="w-8 h-8 rounded-md border border-[#262626]" style={{ background: c }} title={c} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
